import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import StripeCardForm from './StripeCardForm';
import BizumFlow from './BizumFlow';
import { CreditCard, Smartphone, Banknote } from 'lucide-react';
import { startPaymentAndAwaitConfirmation } from '../lib/payments';
import { API_URL } from '../config';
import { buildCheckoutAuthHeaders } from '../utils/checkoutSession';
import ViewportSelect from './ViewportSelect';
import { getMessage } from '../data/locales';
import { getBizumCheckoutErrorKey } from '../utils/bizumCheckout';

// Make sure to use your Publishable Key
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

/** Una sola petición create-intent en vuelo por pedido + tipo (evita rate limit por re-renders / Strict Mode). */
const inflightCreateIntent = new Map();
/** Reutiliza client_secret ya resuelto tras remounts (p. ej. Strict Mode) sin repetir create-intent. */
const resolvedCreateIntent = new Map();

function buildCreateIntentCacheKey(orderId, paymentMethodType, selectedInstallments) {
  return `${orderId}|${paymentMethodType}|${selectedInstallments ?? ''}`;
}

/**
 * Solo red: POST create-intent y parsea JSON. Lanza Error si !res.ok.
 * Sin setState ni callbacks: la promesa en caché es reutilizable entre instancias.
 */
async function fetchCreateIntent(apiUrl, orderId, paymentMethodType, selectedInstallments) {
  const cacheKey = buildCreateIntentCacheKey(orderId, paymentMethodType, selectedInstallments);
  if (resolvedCreateIntent.has(cacheKey)) {
    return resolvedCreateIntent.get(cacheKey);
  }

  const res = await fetch(`${apiUrl}/api/pagos/stripe/create-intent`, {
    method: 'POST',
    headers: buildCheckoutAuthHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify({
      orderId,
      paymentMethodType,
      selectedInstallments,
    }),
  });
  const data = await res.json();
  console.log(`[PaymentMethods] Intent HTTP ${res.status}`, data?.paymentIntentId || '');
  if (!res.ok) {
    const intentError = new Error(data.error || 'Error creating payment intent');
    intentError.code = typeof data.code === "string" ? data.code : "";
    throw intentError;
  }
  resolvedCreateIntent.set(cacheKey, data);
  return data;
}

const PaymentMethods = ({ 
  orderId, 
  userEmail,
  billingDetails,
  installmentOptions, 
  onPaymentSuccess, 
  onPaymentError,
  onStartProcessing
}) => {
  const [method, setMethod] = useState('card'); // 'card', 'bizum', 'financing'
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [selectedInstallments, setSelectedInstallments] = useState(null);
  const [financingProvider, setFinancingProvider] = useState('klarna'); // 'klarna', 'alma'
  const [intentError, setIntentError] = useState(false);
  /** Instancia Stripe resuelta: evita montar Elements con la promesa pendiente (menos fallos en dev/Strict Mode). */
  const [stripe, setStripe] = useState(null);
  /** null = loading; Bizum tab only when true (requires CHECKOUT_BIZUM_ENABLED on backend). */
  const [bizumOffered, setBizumOffered] = useState(null);
  const [bizumGuardError, setBizumGuardError] = useState("");

  const stripeElementsOptions = useMemo(
    () =>
      clientSecret
        ? {
            clientSecret,
            appearance: { theme: 'stripe' },
            loader: 'auto',
          }
        : null,
    [clientSecret]
  );

  const onPaymentErrorRef = useRef(onPaymentError);
  onPaymentErrorRef.current = onPaymentError;

  // Check for missing Stripe key
  useEffect(() => {
    if (!stripeKey) {
        console.warn("Stripe Publishable Key is missing! Check REACT_APP_STRIPE_PUBLISHABLE_KEY.");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!stripePromise) {
      setStripe(null);
      return;
    }
    stripePromise.then((s) => {
      if (!cancelled) setStripe(s);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/pagos/checkout-options`)
      .then((r) => (r.ok ? r.json() : { bizum: false }))
      .then((data) => {
        if (!cancelled) setBizumOffered(Boolean(data?.bizum));
      })
      .catch(() => {
        if (!cancelled) setBizumOffered(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (bizumOffered === false && method === 'bizum') {
      setMethod('card');
    }
  }, [bizumOffered, method]);

  // Reset state when method changes
  useEffect(() => {
    setClientSecret(null);
    setLoading(false);
    setIntentError(false);
    setBizumGuardError("");
  }, [method]);

  // Create Intent: la caché guarda solo la promesa de red; loading/errores son por instancia.
  const createIntent = useCallback(async (type, installments = null) => {
    const cacheKey = buildCreateIntentCacheKey(orderId, type, installments);
    let networkPromise = inflightCreateIntent.get(cacheKey);
    if (!networkPromise) {
      console.log(`[PaymentMethods] Creating intent for type: ${type}`);
      networkPromise = fetchCreateIntent(API_URL, orderId, type, installments).finally(() => {
        inflightCreateIntent.delete(cacheKey);
      });
      inflightCreateIntent.set(cacheKey, networkPromise);
    } else {
      console.log(`[PaymentMethods] Reusing in-flight create-intent for ${cacheKey}`);
    }

    setLoading(true);
    try {
      const data = await networkPromise;
      return data;
    } catch (err) {
      console.error('[PaymentMethods] Create Intent Error:', err);
      const msg = err?.message || 'Error creating payment intent';
      if (typeof onPaymentErrorRef.current === 'function') onPaymentErrorRef.current(msg);
      setIntentError(true);
      return null;
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  // Handle Tab Change
  const handleMethodChange = (newMethod) => {
    setMethod(newMethod);
    setClientSecret(null);
    setIntentError(false);
    // If financing, set default installments
    if (newMethod === 'financing' && installmentOptions && installmentOptions.length > 0) {
       setSelectedInstallments(installmentOptions[0]);
    }
  };

  // Initialize Card Payment (un solo create-intent; cleanup ignora respuestas obsoletas p. ej. Strict Mode)
  useEffect(() => {
    if (method !== 'card' || clientSecret || intentError) return;
    let cancelled = false;
    createIntent('card').then((data) => {
      if (cancelled || !data?.client_secret) return;
      setClientSecret(data.client_secret);
    });
    return () => {
      cancelled = true;
    };
  }, [method, createIntent, clientSecret, intentError]);


  const handleBizumPay = async (bizumPhone) => {
    setBizumGuardError("");
    const billing = {
      ...(billingDetails && typeof billingDetails === "object" ? billingDetails : {}),
      email: (billingDetails && billingDetails.email) || userEmail,
      phone: bizumPhone,
    };
    const bizumErrorKey = getBizumCheckoutErrorKey(billing);
    if (bizumErrorKey) {
      setBizumGuardError(getMessage(bizumErrorKey));
      return;
    }

    const result = await startPaymentAndAwaitConfirmation({
        orderId,
        method: 'bizum',
        billingDetails: billing,
        stripe: await stripePromise,
        onProcessing: onStartProcessing,
        onError: (msg) => {
             if (typeof onPaymentError === 'function') onPaymentError(msg);
             setLoading(false);
        }
    });

    if (result && (result.status === 'processing' || result.status === 'redirecting')) {
         if (typeof onPaymentSuccess === 'function') {
            onPaymentSuccess(result.order);
         }
         // Do not setLoading(false) here if processing, let parent handle it? 
         // Actually, parent will switch UI.
         setLoading(false);
    }
  };

  const handleFinancingPay = async () => {
      if (!selectedInstallments) {
          if (typeof onPaymentError === 'function') onPaymentError("Seleccione plazos");
          return;
      }

      const result = await startPaymentAndAwaitConfirmation({
        orderId,
        method: 'financing',
        billingDetails: billingDetails || { email: userEmail },
        selectedInstallments,
        financingProvider,
        stripe: await stripePromise,
        onProcessing: onStartProcessing,
        onError: (msg) => {
             if (typeof onPaymentError === 'function') onPaymentError(msg);
             setLoading(false);
        }
      });

      if (result && (result.status === 'processing' || result.status === 'redirecting')) {
         if (result.status === 'processing' && typeof onPaymentSuccess === 'function') {
            onPaymentSuccess(result.order);
         }
         if (result.status === 'processing') {
             setLoading(false);
         }
      } else {
         setLoading(false);
      }
  };

  if (!stripeKey) {
     return (
        <div className="alert alert-danger shadow-sm">
            <h4 className="alert-heading">Error de configuración</h4>
            <p>El sistema de pagos no está disponible en este momento. Por favor, contacte con el soporte técnico.</p>
            <hr />
            <p className="mb-0 text-muted small">Missing Stripe Public Key.</p>
        </div>
     );
  }

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white p-0">
        <ul className="nav nav-tabs nav-fill">
          <li className="nav-item">
            <button 
                className={`nav-link border-0 py-3 ${method === 'card' ? 'active fw-bold' : ''}`}
                onClick={() => handleMethodChange('card')}
            >
              <CreditCard className="me-2" size={20} />
              Tarjeta
            </button>
          </li>
          {bizumOffered === true && (
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link border-0 py-3 ${method === 'bizum' ? 'active fw-bold' : ''}`}
                onClick={() => handleMethodChange('bizum')}
              >
                <Smartphone className="me-2" size={20} />
                {getMessage("checkout.bizumTab")}
              </button>
            </li>
          )}
          {installmentOptions && installmentOptions.length > 0 && (
            <li className="nav-item">
                <button 
                    className={`nav-link border-0 py-3 ${method === 'financing' ? 'active fw-bold' : ''}`}
                    onClick={() => handleMethodChange('financing')}
                >
                <Banknote className="me-2" size={20} />
                Financiación
                </button>
            </li>
          )}
        </ul>
      </div>

      <div className="card-body p-4">
        {method === 'card' && (
          <div className="fade-in">
            {clientSecret && stripe && stripeElementsOptions ? (
              <Elements
                key={clientSecret}
                stripe={stripe}
                options={stripeElementsOptions}
              >
                <StripeCardForm 
                    orderId={orderId}
                    clientSecret={clientSecret}
                    userEmail={userEmail}
                    billingDetails={billingDetails}
                    onSuccess={onPaymentSuccess}
                    onError={onPaymentError}
                    onProcessing={onStartProcessing}
                />
              </Elements>
            ) : clientSecret && !stripe ? (
                <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Inicializando Stripe…</span>
                    </div>
                </div>
            ) : intentError ? (
                <div className="text-center py-4">
                    <p className="text-danger mb-3">Error al cargar el formulario de pago.</p>
                    <button 
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setIntentError(false)}
                    >
                        Reintentar
                    </button>
                </div>
            ) : (
                <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                </div>
            )}
          </div>
        )}

        {method === 'bizum' && (
          <BizumFlow
            onStartPayment={handleBizumPay}
            loading={loading}
            initialPhone={billingDetails?.phone || ''}
            guardError={bizumGuardError}
          />
        )}

        {method === 'financing' && (
            <div className="fade-in">
                <h6 className="mb-3">Opciones de Financiación</h6>
                
                <div className="mb-3">
                    <label className="form-label">Proveedor</label>
                    <div className="d-flex gap-3">
                        <div className="form-check border rounded p-3 flex-grow-1">
                            <input 
                                className="form-check-input" 
                                type="radio" 
                                name="provider" 
                                id="klarna" 
                                checked={financingProvider === 'klarna'}
                                onChange={() => setFinancingProvider('klarna')}
                            />
                            <label className="form-check-label w-100" htmlFor="klarna">
                                <strong>Klarna</strong>
                            </label>
                        </div>
                        <div className="form-check border rounded p-3 flex-grow-1">
                            <input 
                                className="form-check-input" 
                                type="radio" 
                                name="provider" 
                                id="alma" 
                                checked={financingProvider === 'alma'}
                                onChange={() => setFinancingProvider('alma')}
                            />
                            <label className="form-check-label w-100" htmlFor="alma">
                                <strong>Alma</strong>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="mb-4">
                    <label className="form-label">Plazos</label>
                    <ViewportSelect 
                        className="form-select"
                        value={selectedInstallments || ''}
                        onChange={(e) => setSelectedInstallments(Number(e.target.value))}
                    >
                        {installmentOptions.map(opt => (
                            <option key={opt} value={opt}>{opt} cuotas</option>
                        ))}
                    </ViewportSelect>
                </div>

                <button 
                   onClick={handleFinancingPay} 
                   className="btn btn-primary w-100 py-2"
                   disabled={loading}
                >
                   {loading ? 'Procesando...' : `Financiar con ${financingProvider === 'klarna' ? 'Klarna' : 'Alma'}`}
               </button>
            </div>
        )}
      </div>
    </div>
  );
};

export default PaymentMethods;
